import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import NavigationHeader from './components/navigationHeader';
import PageContainer from './components/pageContainer';
import PageFooter from './components/pageFooter';
import PageLayout from './components/pageLayout';
import PageBody from './components/pageBody';
import PageText from './components/pageText';
import { useCart } from '../context/CartContext';
import './NailCommissionPage.css';
import { API_URL } from '../config';

const ART_TYPES = ['Digital illustration', 'Sticker design'];
const SIZES = ['Small (5\u00d77")', 'Medium (8\u00d710")', 'Large (11\u00d714")', 'Custom'];

export const ArtCommissionPage = () => {
    const { addToCart } = useCart();
    const [form, setForm] = useState({
        artType: '', size: '', description: '', referenceLinks: '',
        name: '', contactMethod: '', contactInfo: '',
        dateNeeded: '', agree: false,
    });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [refImages, setRefImages] = useState([]);
    const objectUrlsRef = useRef([]);

    useEffect(() => {
        const urls = objectUrlsRef.current;
        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    const handleImageChange = useCallback((e) => {
        const files = Array.from(e.target.files).slice(0, 5 - refImages.length);
        const newUrls = files.map((f) => URL.createObjectURL(f));
        objectUrlsRef.current.push(...newUrls);
        setRefImages((prev) => [...prev, ...files.map((f, i) => ({ file: f, url: newUrls[i] }))].slice(0, 5));
    }, [refImages.length]);
    const removeImage = (idx) => {
        URL.revokeObjectURL(refImages[idx]?.url);
        setRefImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const isRush = (() => {
        if (!form.dateNeeded) return false;
        const needed = new Date(form.dateNeeded + 'T00:00:00');
        const twoWeeks = new Date();
        twoWeeks.setDate(twoWeeks.getDate() + 14);
        return needed <= twoWeeks;
    })();

    const isValid = form.artType && form.size && form.description &&
        form.name && form.contactMethod && form.contactInfo && form.dateNeeded && form.agree;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || submitting) return;
        setSubmitting(true);
        setSubmitError('');

        const price = isRush ? 50 : 30;

        // Upload reference images
        let uploadedFiles = [];
        if (refImages.length > 0) {
            const fd = new FormData();
            refImages.forEach((r) => fd.append('images', r.file));
            try {
                const upRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: fd });
                const upData = await upRes.json();
                uploadedFiles = upData.files || [];
            } catch { /* continue without images */ }
        }

        const commissionData = {
            artType: form.artType,
            size: form.size,
            description: form.description,
            referenceLinks: form.referenceLinks,
            referenceImages: uploadedFiles,
            name: form.name,
            contactMethod: form.contactMethod,
            contactInfo: form.contactInfo,
            rushOrder: isRush,
            dateNeeded: form.dateNeeded,
            price,
        };

        addToCart({
            id: `art-commission-${Date.now()}`,
            title: 'Art Commission',
            price,
            image: require('../images/site/headerimage.jpg'),
            options: commissionData,
            pendingArtCommission: commissionData,
        });
        setSubmitting(false);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <PageLayout><PageContainer><PageBody>
                <NavigationHeader />
                <PageText>
                    <div className="commission-container commission-success">
                        <p>art commission added to cart \u2727</p>
                        <p className="commission-note">joy will reach out via {form.contactMethod.toLowerCase()} to discuss your piece.</p>
                        <div className="commission-success-links">
                            <Link to="/cart" className="commission-btn">go to cart \u2192</Link>
                            <Link to="/shop" className="commission-btn">continue shopping \u2192</Link>
                        </div>
                    </div>
                </PageText>
                <PageFooter />
            </PageBody></PageContainer></PageLayout>
        );
    }

    return (
        <PageLayout>
            <PageContainer>
                <PageBody>
                    <NavigationHeader />
                    <PageText>
                        <div className="commission-container">
                            <h2 className="commission-title">\u27e1\u208a \u22b9 art commission order form \u27e1\u208a \u22b9</h2>
                            <div className="commission-info">
                                <p>custom art made just for you:</p>
                                <ul className="commission-list">
                                    <li>\u2727 digital or traditional pieces</li>
                                    <li>\u2727 sticker designs &amp; illustrations</li>
                                    <li>\u2727 personalized to your vision</li>
                                </ul>
                                <p className="commission-note">starting at $30. final price depends on complexity and size.</p>
                                <p className="commission-note">art pieces take 1\u20133 weeks depending on medium.</p>
                                <p className="commission-note">questions? DM <a href="https://instagram.com/egg.tarty" target="_blank" rel="noopener noreferrer">@egg.tarty</a> on instagram</p>
                            </div>

                            <form onSubmit={handleSubmit} className="commission-form">
                                <div className="form-group">
                                    <label className="form-label">type of art *</label>
                                    <div className="form-radios">
                                        {ART_TYPES.map((opt) => (
                                            <label key={opt} className="form-radio">
                                                <input type="radio" name="artType" value={opt} checked={form.artType === opt} onChange={update('artType')} />
                                                <span>{opt}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">size *</label>
                                    <div className="form-radios">
                                        {SIZES.map((opt) => (
                                            <label key={opt} className="form-radio">
                                                <input type="radio" name="size" value={opt} checked={form.size === opt} onChange={update('size')} />
                                                <span>{opt}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">describe what you'd like *</label>
                                    <textarea placeholder="subject, colors, mood, style, characters, etc." value={form.description} onChange={update('description')} className="form-textarea" rows={4} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">reference images / links</label>
                                    <textarea placeholder="paste pinterest boards, image links, or describe your inspo" value={form.referenceLinks} onChange={update('referenceLinks')} className="form-textarea" rows={2} />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">reference images (up to 5)</label>
                                    <p className="form-hint">upload inspo pics, screenshots, etc.</p>
                                    <label className="commission-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                                        choose files \u2727
                                        <input type="file" accept="image/*" multiple onChange={handleImageChange} style={{ display: 'none' }} />
                                    </label>
                                    {refImages.length > 0 && (
                                        <div className="upload-previews">
                                            {refImages.map((item, i) => (
                                                <div key={i} className="upload-preview">
                                                    <img src={item.url} alt="" />
                                                    <button type="button" className="upload-remove" onClick={() => removeImage(i)}>\u00d7</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">your name *</label>
                                    <input type="text" value={form.name} onChange={update('name')} className="form-input" />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">preferred contact method *</label>
                                    <div className="form-radios">
                                        {['Phone', 'Email'].map((opt) => (
                                            <label key={opt} className="form-radio">
                                                <input type="radio" name="contactMethod" value={opt} checked={form.contactMethod === opt} onChange={(e) => setForm({ ...form, contactMethod: e.target.value, contactInfo: '' })} />
                                                <span>{opt}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {form.contactMethod && (
                                    <div className="form-group">
                                        <label className="form-label">{form.contactMethod === 'Phone' ? 'phone number' : 'email'} *</label>
                                        <input
                                            type={form.contactMethod === 'Phone' ? 'tel' : 'email'}
                                            placeholder={form.contactMethod === 'Phone' ? 'your phone number' : 'your email'}
                                            value={form.contactInfo}
                                            onChange={update('contactInfo')}
                                            className="form-input"
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">date needed by *</label>
                                    <input type="date" value={form.dateNeeded} onChange={update('dateNeeded')} className="form-input" />
                                    {isRush && (
                                        <p className="form-hint" style={{ color: '#b35900', opacity: 1 }}>
                                            \u2727 this is within 2 weeks, rush order fee of +$20 will be applied
                                        </p>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-checkbox">
                                        <input type="checkbox" checked={form.agree} onChange={() => setForm({ ...form, agree: !form.agree })} />
                                        <span>i agree to the processing time and no-refund policy on custom art *</span>
                                    </label>
                                </div>

                                {submitError && <p className="commission-note" style={{ color: '#c0392b' }}>{submitError}</p>}

                                <button type="submit" disabled={!isValid || submitting} className="commission-btn commission-submit-btn">
                                    {submitting ? 'submitting...' : 'add to cart'}
                                </button>
                            </form>
                        </div>
                    </PageText>
                    <PageFooter />
                </PageBody>
            </PageContainer>
        </PageLayout>
    );
};
